/**
 * useGeneratorGallery — reads and curates a generator's photo gallery (gallery blueprint U1).
 *
 * Reads are direct, org-scoped Firestore queries: firestore.rules allows a `generatorPhotos`
 * list only when the query pins `organizationId` to the caller's org. Writes go through the
 * five P11 callables (functions/src/fieldService/gallery/callables.ts); nothing here writes
 * Firestore directly.
 *
 * - `useGeneratorGallery(docId, primaryPhotoId)` — the unit's gallery rows, primary first.
 *   Equality-only, so it needs no composite index.
 * - `useGeneratorPhotoLibrary(docId, filters)` — every indexed photo of the unit, paged by
 *   `capturedAt desc` (composite index in firestore.indexes.json). Duplicates and hidden rows
 *   are left out unless `showHidden`.
 * - `usePromoteGalleryPhotos` / `useHideGalleryPhotos` / `useSetGalleryPrimary` — optimistic on
 *   mutate, rolled back on error, invalidated on settle. Removal with Undo: `useGalleryRemoval`.
 *
 * @example
 * const primaryId = gen._opgUnit?.gallery?.primaryPhotoId ?? null;
 * const { data: photos = [] } = useGeneratorGallery(gen.id, primaryId);
 * const promote = usePromoteGalleryPhotos(gen.id);
 * const { results } = await promote.mutateAsync({ photos: selectedRows });
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import toast from 'react-hot-toast';
import { describeCallableFailure } from '../../lib/offlineErrors';
import { db, functions } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { useOrganization } from '../../contexts/OrganizationContext';
import { generatorKeys, type GeneratorDoc } from '../useGenerator';
import { orderGallery } from '../../lib/fieldService/galleryRows';
import type { GeneratorPhoto } from '../../types/fieldService/generatorPhotos';

export interface LibraryFilters {
  showHidden: boolean;
}

/** Org is part of every key, so switching org never serves the other org's rows from cache. */
export const generatorPhotoKeys = {
  all: ['generatorPhotos'] as const,
  galleries: () => [...generatorPhotoKeys.all, 'gallery'] as const,
  gallery: (organizationId: string, generatorDocId: string) =>
    [...generatorPhotoKeys.galleries(), organizationId, generatorDocId] as const,
  libraries: (organizationId: string, generatorDocId: string) =>
    [...generatorPhotoKeys.all, 'library', organizationId, generatorDocId] as const,
  library: (organizationId: string, generatorDocId: string, filters: LibraryFilters) =>
    [...generatorPhotoKeys.libraries(organizationId, generatorDocId), filters] as const,
  submissions: () => [...generatorPhotoKeys.all, 'submission'] as const,
  /** Library rows indexed from one report (the FSR review picker). */
  submission: (organizationId: string, submissionId: string) =>
    [...generatorPhotoKeys.submissions(), organizationId, submissionId] as const,
};

/** Library rows per page. Tiles show report originals (~1920px), so pages stay small. */
export const LIBRARY_PAGE_SIZE = 24;

export type PromoteOutcome =
  | { photoId: string; outcome: 'promoted' }
  | { photoId: string; outcome: 'already-in-gallery' }
  | { photoId: string; outcome: 'skipped'; reason: string };

export interface PromoteResult {
  results: PromoteOutcome[];
}

export interface LibraryPage {
  rows: GeneratorPhoto[];
  /** Rows on this page the current filter hid (duplicates / removed). Set by the hook's select. */
  hiddenCount?: number;
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

function toPhoto(snap: QueryDocumentSnapshot<DocumentData>): GeneratorPhoto {
  return { ...(snap.data() as Omit<GeneratorPhoto, 'id'>), id: snap.id };
}

async function fetchGallery(organizationId: string, generatorDocId: string): Promise<GeneratorPhoto[]> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTIONS.GENERATOR_PHOTOS),
      where('organizationId', '==', organizationId),
      where('generatorDocId', '==', generatorDocId),
      where('state', '==', 'gallery')
    )
  );
  return snap.docs.map(toPhoto);
}

export function useGeneratorGallery(generatorDocId: string | undefined, primaryPhotoId: string | null | undefined) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  return useQuery({
    queryKey: generatorPhotoKeys.gallery(orgId, generatorDocId ?? ''),
    queryFn: () => fetchGallery(orgId, generatorDocId as string),
    enabled: !!orgId && !!generatorDocId,
    select: (rows: GeneratorPhoto[]) => orderGallery(rows, primaryPhotoId),
  });
}

/** Rows the "All photos" view hides unless asked: dedupe twins and removed photos. */
export function isShownInLibrary(row: GeneratorPhoto, filters: LibraryFilters): boolean {
  return filters.showHidden || (row.duplicateOf == null && row.state !== 'hidden');
}

async function fetchLibraryPage(
  organizationId: string,
  generatorDocId: string,
  cursor: QueryDocumentSnapshot<DocumentData> | null
): Promise<LibraryPage> {
  const parts = [
    where('organizationId', '==', organizationId),
    where('generatorDocId', '==', generatorDocId),
    orderBy('capturedAt', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(LIBRARY_PAGE_SIZE),
  ];
  const snap = await getDocs(query(collection(db, COLLECTIONS.GENERATOR_PHOTOS), ...parts));
  return {
    rows: snap.docs.map(toPhoto),
    cursor: snap.docs[snap.docs.length - 1] ?? null,
    hasMore: snap.docs.length === LIBRARY_PAGE_SIZE,
  };
}

export function useGeneratorPhotoLibrary(generatorDocId: string | undefined, filters: LibraryFilters, enabled = true) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  return useInfiniteQuery({
    queryKey: generatorPhotoKeys.library(orgId, generatorDocId ?? '', filters),
    queryFn: ({ pageParam }) => fetchLibraryPage(orgId, generatorDocId as string, pageParam),
    initialPageParam: null as QueryDocumentSnapshot<DocumentData> | null,
    getNextPageParam: (last) => (last.hasMore ? last.cursor : undefined),
    enabled: enabled && !!orgId && !!generatorDocId,
    select: (data) => ({
      ...data,
      pages: data.pages.map((p) => {
        const rows = p.rows.filter((r) => isShownInLibrary(r, filters));
        return { ...p, rows, hiddenCount: p.rows.length - rows.length };
      }),
    }),
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const promoteFn = httpsCallable<{ organizationId: string; photoIds: string[] }, PromoteResult>(
  functions,
  'fieldService_galleryPromote'
);
const hideFn = httpsCallable<{ organizationId: string; photoId: string }, { changed: boolean }>(
  functions,
  'fieldService_galleryHide'
);
const setPrimaryFn = httpsCallable<
  { organizationId: string; generatorDocId: string; photoId: string },
  { primaryPhotoId: string }
>(functions, 'fieldService_gallerySetPrimary');

type LibraryCache = InfiniteData<LibraryPage, unknown>;
type Snapshot = Array<[readonly unknown[], unknown]>;

/**
 * Callable codes whose message the gallery server wrote for a person to read
 * (galleryService.ts: photo ids and plain reasons, never internals). Anything else, including
 * `internal`, gets the standard offline / unavailable / fallback wording.
 */
const USER_FACING_CODES = new Set([
  'functions/permission-denied',
  'functions/invalid-argument',
  'functions/failed-precondition',
  'functions/not-found',
  'functions/aborted',
]);

export function galleryErrorMessage(err: unknown, fallback: string): string {
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code === 'string' && USER_FACING_CODES.has(code) && err instanceof Error && err.message) {
    return err.message;
  }
  // Gallery writes (hide, promote, primary, move, plate component) go straight to the server and keep nothing on the
  // device; the pin queue has its own wording (PIN_QUEUE_SAVED_MESSAGE), so this must not promise "saved on this phone".
  return describeCallableFailure(err, fallback, undefined, { workKeptOnDevice: false }).message;
}

export function useGalleryCache(organizationId: string, generatorDocId: string) {
  const queryClient = useQueryClient();
  const galleryKey = generatorPhotoKeys.gallery(organizationId, generatorDocId);
  const librariesKey = generatorPhotoKeys.libraries(organizationId, generatorDocId);
  const generatorKey = generatorKeys.detail(generatorDocId);

  return {
    queryClient,
    galleryKey,
    async snapshot(): Promise<Snapshot> {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: galleryKey }),
        queryClient.cancelQueries({ queryKey: librariesKey }),
        queryClient.cancelQueries({ queryKey: generatorKey }),
      ]);
      return [
        [galleryKey, queryClient.getQueryData(galleryKey)],
        [generatorKey, queryClient.getQueryData(generatorKey)],
        ...queryClient.getQueriesData({ queryKey: librariesKey }),
      ];
    },
    restore(snapshot: Snapshot | undefined) {
      for (const [key, data] of snapshot ?? []) queryClient.setQueryData(key, data);
    },
    patchLibraries(fn: (row: GeneratorPhoto) => GeneratorPhoto) {
      queryClient.setQueriesData<LibraryCache>({ queryKey: librariesKey }, (old) =>
        old ? { ...old, pages: old.pages.map((p) => ({ ...p, rows: p.rows.map(fn) })) } : old
      );
    },
    invalidateAll() {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: galleryKey }),
        queryClient.invalidateQueries({ queryKey: librariesKey }),
        queryClient.invalidateQueries({ queryKey: generatorKey }),
      ]);
    },
  };
}

/** F9 promote: library -> gallery. Send only rows `batchBlocker` lets through (F-11). */
export function usePromoteGalleryPhotos(generatorDocId: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const cache = useGalleryCache(orgId, generatorDocId);

  return useMutation({
    mutationFn: async ({ photos }: { photos: GeneratorPhoto[] }) =>
      (await promoteFn({ organizationId: orgId, photoIds: photos.map((p) => p.id) })).data,
    onMutate: async ({ photos }) => {
      const snapshot = await cache.snapshot();
      const ids = new Set(photos.map((p) => p.id));
      cache.queryClient.setQueryData<GeneratorPhoto[]>(cache.galleryKey, (old) => {
        const present = new Set((old ?? []).map((r) => r.id));
        const added = photos.filter((p) => !present.has(p.id)).map((p) => ({ ...p, state: 'gallery' as const }));
        return [...(old ?? []), ...added];
      });
      cache.patchLibraries((row) => (ids.has(row.id) ? { ...row, state: 'gallery' } : row));
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      cache.restore(ctx?.snapshot);
      toast.error(galleryErrorMessage(err, 'Could not add the photos to the gallery.'));
    },
    onSettled: () => cache.invalidateAll(),
  });
}

export interface HideResult {
  /** Photos that left the gallery. */
  removed: string[];
  /** Photos the server refused, with its plain-language reason. */
  failed: Array<{ photoId: string; message: string }>;
}

/**
 * F9 hide, for one photo or several: gallery -> hidden. The durable copy stays, so Undo can
 * put a photo back. Every photo leaves the screen at once; the server calls run one after
 * another (each rewrites the unit's gallery summary in a transaction, so running them side by
 * side would only make them retry each other). A refusal of some photos does not stop the
 * rest: those come back in `failed`, and the settle refetch puts them back on screen.
 */
export function useHideGalleryPhotos(generatorDocId: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const cache = useGalleryCache(orgId, generatorDocId);

  return useMutation({
    mutationFn: async ({ photoIds }: { photoIds: string[] }): Promise<HideResult> => {
      const result: HideResult = { removed: [], failed: [] };
      for (const photoId of photoIds) {
        try {
          await hideFn({ organizationId: orgId, photoId });
          result.removed.push(photoId);
        } catch (err) {
          result.failed.push({ photoId, message: galleryErrorMessage(err, 'Could not remove the photo.') });
        }
      }
      if (result.removed.length === 0 && result.failed.length > 0) {
        throw new Error(photoIds.length === 1 ? result.failed[0].message : 'Could not remove the photos from the gallery.');
      }
      return result;
    },
    onMutate: async ({ photoIds }) => {
      const snapshot = await cache.snapshot();
      const ids = new Set(photoIds);
      cache.queryClient.setQueryData<GeneratorPhoto[]>(cache.galleryKey, (old) => (old ?? []).filter((r) => !ids.has(r.id)));
      cache.patchLibraries((row) => (ids.has(row.id) ? { ...row, state: 'hidden' } : row));
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      cache.restore(ctx?.snapshot);
      toast.error(err instanceof Error && err.message ? err.message : 'Could not remove the photos from the gallery.');
    },
    onSuccess: ({ failed }) => {
      if (failed.length > 0) {
        toast.error(`${failed.length} photo${failed.length === 1 ? '' : 's'} could not be removed: ${failed[0].message}`);
      }
    },
    onSettled: () => cache.invalidateAll(),
  });
}

/** F9 setPrimary: only a gallery photo of this unit. */
export function useSetGalleryPrimary(generatorDocId: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const cache = useGalleryCache(orgId, generatorDocId);

  return useMutation({
    mutationFn: async ({ photoId }: { photoId: string }) =>
      (await setPrimaryFn({ organizationId: orgId, generatorDocId, photoId })).data,
    onMutate: async ({ photoId }) => {
      const snapshot = await cache.snapshot();
      const thumb =
        cache.queryClient.getQueryData<GeneratorPhoto[]>(cache.galleryKey)?.find((r) => r.id === photoId)?.gallery
          ?.durable.thumb ?? null;
      cache.queryClient.setQueryData<GeneratorDoc | null>(generatorKeys.detail(generatorDocId), (old) => {
        const summary = old?._opgUnit?.gallery;
        if (!old || !summary) return old;
        return {
          ...old,
          _opgUnit: { ...old._opgUnit, gallery: { ...summary, primaryPhotoId: photoId, primaryThumbPath: thumb } },
        };
      });
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      cache.restore(ctx?.snapshot);
      toast.error(galleryErrorMessage(err, 'Could not set the primary photo.'));
    },
    onSettled: () => cache.invalidateAll(),
  });
}
